import '@kerfjs/ui/select/register';

import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { Select } from '@kerfjs/ui/select';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';

import type { LineOrderT } from '../../../data/api.js';

/** A date input's `YYYY-MM-DD` value for an ISO timestamp, or '' when absent. */
export function dateInputValue(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

/**
 * Mark a decided line Ordered, or edit its order (FR-5.18, HS2-HYDRJX): the PO reference, order
 * date, expected delivery, and the vendor ordered from. The requester sees the PO and expected
 * delivery on the line. An existing order can be undone.
 */
export function OrderDialog({
  lineNumber,
  lineDescription,
  order,
  vendorOptions,
  defaultVendorId,
  today,
  failing = false,
}: {
  lineNumber: number;
  lineDescription: string;
  order: LineOrderT | undefined;
  vendorOptions: readonly { vendorId: string; vendorName: string }[];
  defaultVendorId: string;
  /** Today as `YYYY-MM-DD`, the default order date. */
  today: string;
  /** Asking why the order failed, before re-sourcing the line (FR-5.21, HS2-SFY5DY). */
  failing?: boolean;
}): SafeHtml {
  if (failing && order !== undefined) return <OrderFailedDialog lineNumber={lineNumber} order={order} />;
  const vendorId = order?.vendorId ?? defaultVendorId;
  return (
    <DialogSurface
      label={`${order === undefined ? 'Mark Ordered' : 'Edit Order'} · Line ${String(lineNumber)}`}
      className="order-dialog"
      lightDismiss
      rootAttributes={{ 'data-unsaved-guard': 'this order', 'data-key': 'order-edit', 'data-auto-open-dialog': '' }}
      footer={
        <>
          {order !== undefined ? (
            <Button size="default" tone="danger" emphasis="plain" action="order-clear">
              Undo Ordered
            </Button>
          ) : null}
          {order !== undefined && order.receipts.length === 0 ? (
            <Button size="default" tone="danger" emphasis="plain" action="order-fail-open">
              Order Failed…
            </Button>
          ) : null}
          <Button
            size="default"
            tone="neutral"
            emphasis="plain"
            action="order-cancel"
            rootAttributes={{ 'data-unsaved-cancel': '', 'data-dialog-close': '' }}
          >
            Cancel
          </Button>
          <Button
            size="default"
            tone="brand"
            emphasis="primary"
            action="order-save"
            rootAttributes={{ 'data-unsaved-save': '' }}
          >
            {order === undefined ? 'Mark Ordered' : 'Save Order'}
          </Button>
        </>
      }
    >
      <List gap="m">
        <Text variant="p">{`Record the purchase order for “${lineDescription}”. The requester sees the PO number and expected delivery on the line.`}</Text>
        <TextField name="order-po" label="PO Number" maxLength={80} value={order?.poNumber ?? ''} autofocus />
        <TextField type="date" name="order-date" label="Order Date" value={dateInputValue(order?.orderedAt) || today} />
        <TextField
          type="date"
          name="order-expected"
          label="Expected Delivery"
          hint="Optional"
          value={dateInputValue(order?.expectedDeliveryAt)}
        />
        {vendorOptions.length > 0 ? (
          <Select
            name="order-vendor"
            value={vendorId}
            label="Vendor"
            choices={[
              { value: '', label: 'Vendor (from Decision)' },
              ...vendorOptions.map((vendor) => ({ value: vendor.vendorId, label: vendor.vendorName })),
            ]}
          />
        ) : null}
        <TextField
          multiline
          name="order-note"
          label="Note"
          hint="Staff only"
          rows={2}
          maxLength={1000}
          value={order?.note ?? ''}
        />
      </List>
    </DialogSurface>
  );
}

/**
 * The vendor failed to deliver (FR-5.21, HS2-SFY5DY): record why, then re-source the line. Its
 * purchase decision (and any outcome) is cleared, so the line is decided again, preferring another
 * vendor; the failed order stays in the line's history.
 */
function OrderFailedDialog({ lineNumber, order }: { lineNumber: number; order: LineOrderT }): SafeHtml {
  return (
    <DialogSurface
      label={`Order Failed · Line ${String(lineNumber)}`}
      className="order-dialog"
      lightDismiss
      rootAttributes={{ 'data-unsaved-guard': 'this failure', 'data-key': 'order-failed', 'data-auto-open-dialog': '' }}
      footer={
        <>
          <Button
            size="default"
            tone="neutral"
            emphasis="plain"
            action="order-cancel"
            rootAttributes={{ 'data-unsaved-cancel': '', 'data-dialog-close': '' }}
          >
            Cancel
          </Button>
          <Button
            size="default"
            tone="danger"
            emphasis="primary"
            action="order-fail"
            rootAttributes={{ 'data-unsaved-save': '' }}
          >
            Re-Source Line
          </Button>
        </>
      }
    >
      <List gap="m">
        <Text variant="p">
          {`The line goes back to be decided again, with ${order.vendorName ?? 'this vendor'} ranked last. Its decision and any recorded outcome are cleared; the failed order stays in its history.`}
        </Text>
        <TextField multiline name="order-fail-reason" label="What Went Wrong" rows={3} maxLength={500} autofocus />
      </List>
    </DialogSurface>
  );
}
